import { FormGroup } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { IVendorMapping } from '@xilo-mono/integration-contracts';
import _, { cloneDeep, get } from 'lodash';
import { Observable, of, throwError } from 'rxjs';
import { delay, mergeMap, retryWhen } from 'rxjs/operators';

const helper = new JwtHelperService();

export const flattenFormQuestions = (fields: FormlyFieldConfig[]) => {
  let flattenedForm = [];
  if (fields && fields.length > 0) {
    flattenedForm = fields.reduce(function iter(r, a) {
      if (Array.isArray(a.fieldGroup)) {
        return a.fieldGroup.reduce(iter, r);
      }
      r = {
        ...r,
        [a.key as string]: {
          ...a,
        },
      };
      return r;
    }, {});
  }
  return flattenedForm;
};

export const flattenFormSections = (fields: FormlyFieldConfig[]) => {
  let flattenedForm = [];
  const fieldCopy = cloneDeep(fields);
  if (fieldCopy && fieldCopy.length > 0) {
    flattenedForm = fieldCopy.reduce(function iter(r, a) {
      if (a.type !== 'section' && a.type !== 'intake-section') {
        if (a.type === 'intake-repeat') {
          if (a.fieldGroup[0]) {
            a.fieldGroup[0].key = a.key;
          }
        }
        return a.fieldGroup.reduce(iter, r);
      }
      r.push(a);
      return r;
    }, []);
  }
  return flattenedForm;
};

export const getArrayOfSectionQuestions = (fields: FormlyFieldConfig[]) => {
  const sectionQuestions = {};
  for (const field of fields) {
    const questions = field.fieldArray
      ? flattenFormQuestions(field.fieldArray.fieldGroup)
      : flattenFormQuestions(field.fieldGroup);
    if (
      !sectionQuestions[field.key as string] ||
      sectionQuestions[field.key as string].length === 0
    ) {
      sectionQuestions[field.key as string] = [];
    }
    for (const key in questions) {
      if (questions[key]) {
        sectionQuestions[field.key as string].push(questions[key]);
      }
    }
  }

  return sectionQuestions;
};

export const getArrayOfSectionsAndFlatQuestions = (
  fields: FormlyFieldConfig[]
) => {
  const sectionQuestions = [];
  for (const field of fields) {
    const questions = field.fieldArray
      ? flattenFormQuestions(field.fieldArray.fieldGroup)
      : flattenFormQuestions(field.fieldGroup);
    for (const key in questions) {
      if (questions[key]) {
        questions[key]['sectionLabel'] = field.templateOptions.label;
        questions[key]['sectionKey'] = field.key;
        questions[key] = {
          ...questions[key],
          ...questions[key].templateOptions,
        };
        delete questions[key].templateOptions;
        sectionQuestions.push(questions[key]);
      }
    }
  }

  return sectionQuestions;
};

export const getFormFieldPath = (
  isIntakeForm: boolean,
  fields: FormlyFieldConfig[],
  key: string
) => {
  let path = null;

  // tslint:disable-next-line:no-shadowed-variable
  function loopThroughFields(sectionKey, fieldGroup) {
    fieldGroup.forEach((field) => {
      if (isIntakeForm && field.key === key) {
        path = `${sectionKey}.${field.key}`;
      } else if (!isIntakeForm) {
        field.fieldGroup.forEach((question) => {
          if (question.key === key) {
            path = `${sectionKey}.${field.key}.${question.key}`;
          }
        });
      }
    });
  }

  for (const component of fields) {
    let fieldGroup;
    if (component.fieldGroup) {
      fieldGroup = component.fieldGroup;
    } else if (component.fieldArray) {
      fieldGroup = component.fieldArray.fieldGroup;
    }
    loopThroughFields(component.key, fieldGroup);
  }

  return path;
};

export function concatPath(parentKey: string, childKey: string) {
  if (parentKey) {
    return `${parentKey}.${childKey}`;
  } else {
    return childKey;
  }
}

export function getFormComponents(form: any) {
  return (
    (form &&
      form.components &&
      form.components[0] &&
      form.components[0].fieldGroup) ||
    []
  );
}

export function isFormFieldDataKey(xiloField: any, param: string) {
  return (
    xiloField &&
    xiloField.templateOptions &&
    xiloField.templateOptions.dataKey &&
    xiloField.templateOptions.dataKey === param
  );
}

export function getNewVendorMapping(
  xiloFieldKey: string,
  vendorField: any,
  formPath: string,
  vendorPath: string,
  xiloFieldLabel: any,
  vendorSectionType: 'array' | 'object',
  xiloSectionType?: string,
  xiloFieldTemplateOptions?: any,
  enumMapping?: any[]
): IVendorMapping {
  const formSectionType = xiloSectionType?.includes('repeat')
    ? 'array'
    : 'object';
  const vendorMapping: IVendorMapping = {
    vendorPath,
    vendorFieldType: vendorField.enum ? 'enum' : vendorField.type,
    vendorSectionType: vendorSectionType,
    staticValue: null,
    staticArrayIndex: null,
    formFieldKey: xiloFieldKey,
    formFieldLabel: xiloFieldLabel,
    formSectionType,
    formPath,
  };

  if (get(enumMapping, 'length') > 0) {
    vendorField.enumMapping = enumMapping;
    _.set(vendorMapping, 'enumMap', enumMapping);
  }

  if (xiloFieldTemplateOptions?.options) {
    _.set(vendorMapping, 'options', xiloFieldTemplateOptions.options);
  }

  return vendorMapping;
}

export interface MappingValidationResult {
  valid: boolean;
  hasError?: boolean;
  error?: string;
  enumRemappingValues?: unknown[];
  hasWarning?: boolean;
  warning?: string;
}

export function validateXiloToVendorFieldMapping(
  xiloField: any,
  vendorField: any,
  staticValue: any,
): MappingValidationResult {
  if (vendorField.subType) {
    const dataKeysList = [
      { value: 'industry', display: 'Industry' },
      { value: 'vehicleYear', display: 'Vehicle Year' },
      { value: 'vehicleMake', display: 'Vehicle Make' },
      { value: 'vehicleModel', display: 'Vehicle Model' },
      { value: 'vehicleBodyStyle', display: 'Vehicle Body Style' },
      { value: 'vin', display: 'VIN' },
    ];
    for (const obj of dataKeysList) {
      if (
        vendorField.subType === obj.value &&
        !isFormFieldDataKey(xiloField, obj.value)
      ) {
        return {
          valid: false,
          error: `This field must be an ${obj.display} field`,
          hasError: true,
        };
      }
    }
  } else if (vendorField.enum) {
    if (
      xiloField &&
      !(
        xiloField.type === 'radio' ||
        xiloField.type === 'select' ||
        xiloField.type === 'selectbox'
      )
    ) {
      return {
        valid: false,
        error: 'This field must be a select menu or radio',
        hasError: true,
      }
    } else if (xiloField) {
      const options = xiloField.templateOptions.options;
      const values = options.map((opt) => opt.value);
      let hasMisMatch = false;
      const misMatchedValues = [];
      for (const value of values) {
        if (!vendorField.enum.includes(value)) {
          const valueIsInEnums = isMatch(value, vendorField.enum, 0.75);
          if (valueIsInEnums.status !== true) {
            hasMisMatch = true;
            misMatchedValues.push(value);
          }
        }
      }
      if (!hasMisMatch) {
        return {
          valid: true,
        };
      }
      return {
        valid: false,
        enumRemappingValues: values,
      }
    } else if (staticValue) {
      if (!vendorField.enum.includes(staticValue)) {
        const valueIsInEnums = isMatch(
          staticValue,
          vendorField.enum,
          0.75
        );
        if (valueIsInEnums.status !== true) {
          return {
            valid: false,
            error: `Option ${staticValue} is not valid for this field`,
            hasError: true,
          }
        }
      }
    }
  }
  const result: MappingValidationResult = {
    valid: true,
  };
  const xiloFieldOptionsValue = xiloField.templateOptions.label;

  if (vendorField.type === 'integer') {
    if (xiloField) {
      if (xiloField.type === 'input') {
        if (xiloField.templateOptions.type !== 'number') {
          return {
            valid: false,
            error: 'This field must be a number field and not a text field',
            hasError: true,
          }
        }
        if (vendorField.minimum || vendorField.maximum) {
          if (vendorField.maximum !== xiloField.templateOptions.max) {
            result.warning = `This field has a maximum of ${vendorField.maximum}. We suggest adding this to your form field`;
            result.hasWarning = true;
          } else if (vendorField.minimum !== xiloField.templateOptions.min) {
            result.warning = `This field has a minimum of ${vendorField.minimum}. We suggest adding this to your form field`;
            result.hasWarning = true;
          }
        }
      } else {
        if (
          xiloField.type === 'select' ||
          xiloField.type === 'selectbox' ||
          xiloField.type === 'radio'
        ) {
          if (
            xiloField.templateOptions?.options?.length > 0 &&
            xiloField.templateOptions.options.some((option) =>
              isNaN(option.value)
            )
          ) {
            return {
              valid: false,
              error: 'This field must have number values',
              hasError: true,
            };
          }
        } else {
          return {
            valid: false,
            error: 'This field must be a number field',
            hasError: true,
          };
        }
      }
    } else if (
      (vendorField.minimum || vendorField.maximum) && staticValue
    ) {
      if (vendorField.maximum < +xiloFieldOptionsValue) {
        result.error = `This field has a maximum of ${vendorField.maximum}. Your number is too large`;
        result.hasError = true;
        result.valid = false;
      } else if (vendorField.minimum > +xiloFieldOptionsValue) {
        result.error = `This field has a minimum of ${vendorField.minimum}. Your number is too small`;
        result.hasError = true;
        result.valid = false;
      }
    }
  }
  if (vendorField.type === 'string') {
    if (xiloField) {
      if (vendorField.minLength || vendorField.maxLength) {
        if (vendorField.maxLength !== xiloField.templateOptions.max) {
          result.warning = `This field has a maximum length of ${vendorField.maxLength}. We suggest adding this to your form field`;
          result.hasWarning = true;
        } else if (vendorField.minLength !== xiloField.templateOptions.min) {
          result.warning = `This field has a minimum length of ${vendorField.minLength}. We suggest adding this to your form field`;
          result.hasWarning = true;
        }
      }
    } else if (
      (vendorField.minLength || vendorField.maxLength) && staticValue
    ) {
      if (vendorField.maxLength < xiloFieldOptionsValue.length) {
        result.error = `This field has a max length of ${vendorField.maxLength}. Your text is too long`;
        result.hasError = true;
        result.valid = false;
      } else if (vendorField.minLength > xiloFieldOptionsValue.length) {
        result.error = `This field has a min length of ${vendorField.minLength}. Your text is too short`;
        result.hasError = true;
        result.valid = false;
      }
    }
  }
  if (vendorField.type === 'date') {
    if (
      !(
        xiloField.templateOptions && xiloField.templateOptions.type === 'date'
      )
    ) {
      result.error = 'This field must be a date type';
      result.hasError = true;
      result.valid = false;
      return result;
    } else if (
      (vendorField.minLength || vendorField.maxLength) && staticValue
    ) {
      if (vendorField.maxLength < xiloFieldOptionsValue.length) {
        result.warning = `This field has a max length of ${vendorField.maxLength}. Your text is too long`;
        result.hasWarning = true;
      } else if (vendorField.minLength > xiloFieldOptionsValue.length) {
        result.warning = `This field has a min length of ${vendorField.minLength}. Your text is too short`;
        result.hasWarning = true;
      }
    }
  }

  return result;
}

export function findFormField(key: string, formGroup: FormlyFieldConfig[]): FormlyFieldConfig | undefined {
  if (!key || !formGroup || !Array.isArray(formGroup)) {
    return undefined;
  }
  const field = formGroup.find((g) => g.key === key);
  if (field) {
    return field;
  }
  for (const group of formGroup) {
    const f = group.fieldArray
      ? findFormField(key, group.fieldArray.fieldGroup)
      : findFormField(key, group.fieldGroup);
    if (f) {
      return f;
    }
  }
  return undefined;
}

export function findFormFieldByPath(path: string, formGroup: FormlyFieldConfig[]): FormlyFieldConfig | undefined {
  if (!path || !formGroup || !Array.isArray(formGroup)) {
    return undefined;
  }
  if (path.includes('.')) {
    const splitPath = path.split('.');
    if (splitPath.length > 1) {
      const groupKey = splitPath[0];
      const group = formGroup.find((g) => g.key === groupKey);
      if (group) {
        const restPath = splitPath.slice(1).join('.');
        return findFormFieldByPath(restPath, group.fieldArray ? group.fieldArray.fieldGroup : group.fieldGroup);
      }
    }
  }
  return formGroup.find((g) => g.key === path);
}

export const getFormFieldByKey = (
  isIntakeForm: boolean,
  formGroup: FormGroup,
  path: string,
  subsection: number
) => {
  try {
    const splitPath = path.split('.');
    const sectionKey = splitPath[0];
    const sectionFormGroup = formGroup.controls[sectionKey] as FormGroup;
    if (!sectionFormGroup) {
      return { success: false, error: 'Section is hidden' };
    }
    const fieldKey = splitPath[splitPath.length - 1];
    let fieldFormGroup = null;
    if (!isIntakeForm) {
      if (Array.isArray(sectionFormGroup.controls)) {
        const subsectionFormGroup = sectionFormGroup.controls[
          subsection
          ] as FormGroup;
        const questionGroupKey = splitPath[1];
        const questionGroupFormGroup = subsectionFormGroup.controls[
          questionGroupKey
          ] as FormGroup;
        if (!questionGroupFormGroup)
          return { success: false, error: 'Question group is hidden' };
        fieldFormGroup = questionGroupFormGroup.controls[fieldKey] as FormGroup;
      } else {
        const questionGroupKey = splitPath[1];
        const questionGroupFormGroup = sectionFormGroup.controls[
          questionGroupKey
          ] as FormGroup;
        if (!questionGroupFormGroup)
          return { success: false, error: 'Question group is hidden' };
        fieldFormGroup = questionGroupFormGroup.controls[fieldKey] as FormGroup;
      }
    } else {
      if (Array.isArray(sectionFormGroup.controls)) {
        const subsectionFormGroup = sectionFormGroup.controls[
          subsection
          ] as FormGroup;
        fieldFormGroup = subsectionFormGroup.controls[fieldKey] as FormGroup;
      } else {
        fieldFormGroup = sectionFormGroup.controls[fieldKey] as FormGroup;
      }
    }

    if (!fieldFormGroup) return { success: false, error: 'Field is hidden' };

    return {
      success: true,
      fieldFormGroup,
    };
  } catch (error) {
    return {
      success: false,
      error,
    };
  }
};

const loopThroughFields = (
  sectionKey,
  sectionLabel,
  fieldGroup,
  isArray,
  isIntakeForm
) => {
  const newFieldGroup = fieldGroup.map((field) => {
    if (isIntakeForm) {
      const fieldLabel =
        field.templateOptions.text || field.templateOptions.label;
      const fieldObj = {
        fieldKey: field.key,
        sectionKey: sectionKey,
        dataKey: field?.templateOptions?.dataKey,
        questionKey: null,
        fieldLabel: fieldLabel,
        sectionLabel: sectionLabel,
        type: field.type,
        isArray,
      };

      if (
        field.templateOptions.type &&
        field.templateOptions.type === 'email'
      ) {
        fieldObj['type'] = 'email';
      }

      return fieldObj;
    } else {
      const questionKey = field.key;
      const newField = field.fieldGroup.map((question) => {
        const fieldLabel =
          question.templateOptions.text || question.templateOptions.label;
        const fieldObj = {
          fieldKey: question.key,
          sectionKey: sectionKey,
          dataKey: question?.templateOptions?.dataKey,
          questionKey: questionKey,
          fieldLabel: fieldLabel,
          sectionLabel: sectionLabel,
          isArray,
          type: question.type,
        };

        if (
          question.templateOptions.type &&
          question.templateOptions.type === 'email'
        ) {
          fieldObj['type'] = 'email';
        }

        return fieldObj;
      });
      return newField;
    }
  });

  return newFieldGroup;
};

export const getFlattenedFieldsWithDetails = (
  formType: string,
  fields: FormlyFieldConfig[],
  key?: string
) => {
  const isIntakeForm = formType === 'intake-form';
  const flattenedFields = [];
  for (const component of fields) {
    if (!key || (key && component.key === key)) {
      if (component.fieldArray) {
        const fieldGroup = component.fieldArray.fieldGroup;
        const fieldObj = loopThroughFields(
          component.key,
          component.templateOptions.label,
          fieldGroup,
          true,
          isIntakeForm
        );
        flattenedFields.push(fieldObj);
      } else if (component.fieldGroup) {
        const fieldObj = loopThroughFields(
          component.key,
          component.templateOptions.label,
          component.fieldGroup,
          false,
          isIntakeForm
        );
        flattenedFields.push(fieldObj);
      }
    }
  }

  return flattenedFields;
};

export const getFlattenedArraySectionsWithDetails = (
  fields: FormlyFieldConfig[]
) => {
  const sections = [];

  for (const component of fields) {
    if (component.fieldArray) {
      sections.push({
        label: component.templateOptions.label,
        key: component.key,
      });
    }
  }

  return sections;
};

export const getTemplateOptionsByParam = (
  fields: FormlyFieldConfig[],
  currentKey: string,
  param: string
) => {
  let templateOptions = null;

  for (const component of fields) {
    let fieldGroup = null;
    if (component.fieldGroup) {
      fieldGroup = component.fieldGroup;
    } else if (component.fieldArray) {
      fieldGroup = component.fieldArray.fieldGroup;
    }

    fieldGroup.forEach((field) => {
      if (field.key !== currentKey && field[param]) {
        templateOptions = field.templateOptions;
      }
    });
  }

  return templateOptions;
};

export const getFormType = (form: any) => {
  if (form?.metadata?.type) {
    return form?.metadata?.type;
  } else if (form?.components?.length > 0) {
    return form.components[0].type;
  } else {
    return null;
  }
};

export const compareTwoStrings = (first, second) => {
  first = first.replace(/\s+/g, '');
  second = second.replace(/\s+/g, '');

  if (!first.length && !second.length) return 1; // if both are empty strings
  if (!first.length || !second.length) return 0; // if only one is empty string
  if (first === second) return 1; // identical
  if (first.length === 1 && second.length === 1) return 0; // both are 1-letter strings
  if (first.length < 2 || second.length < 2) return 0; // if either is a 1-letter string

  const firstBigrams = new Map();
  for (let i = 0; i < first.length - 1; i++) {
    const bigram = first.substring(i, i + 2);
    const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) + 1 : 1;

    firstBigrams.set(bigram, count);
  }

  let intersectionSize = 0;
  for (let i = 0; i < second.length - 1; i++) {
    const bigram = second.substring(i, i + 2);
    const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) : 0;

    if (count > 0) {
      firstBigrams.set(bigram, count - 1);
      intersectionSize++;
    }
  }

  return (2.0 * intersectionSize) / (first.length + second.length - 2);
};

export const findBestMatch = (mainString, targetStrings) => {
  if (!areArgsValid(mainString, targetStrings))
    throw new Error(
      'Bad arguments: First argument should be a string, second should be an array of strings'
    );

  const ratings = [];
  let bestMatchIndex = 0;
  mainString = mainString.toLowerCase();
  const lTargetStrings = targetStrings.map((string) => string.toLowerCase());
  for (let i = 0; i < lTargetStrings.length; i++) {
    const currentTargetString = lTargetStrings[i];
    const valueString = targetStrings[i];
    const currentRating = compareTwoStrings(mainString, currentTargetString);
    ratings.push({ target: valueString, rating: currentRating });
    if (currentRating > ratings[bestMatchIndex].rating) {
      bestMatchIndex = i;
    }
  }

  const bestMatch = ratings[bestMatchIndex];

  return { ratings, bestMatch, bestMatchIndex };
};

export const areArgsValid = (mainString, targetStrings) => {
  if (typeof mainString !== 'string') return false;
  if (!Array.isArray(targetStrings)) return false;
  if (!targetStrings.length) return false;
  if (targetStrings.find((s) => typeof s !== 'string')) return false;
  return true;
};

export const isMatch = (
  value: string | number,
  array: string[] = [],
  threshold = 0.5
) => {
  if (array.length !== 0) {
    const ratingsObjs = findBestMatch(value, array);
    const bestMatch = ratingsObjs.bestMatch;
    const ratings = ratingsObjs.ratings.map((ratingObj) => ratingObj.rating);
    const matches = ratings.map((rate) => rate > threshold);
    if (!matches.includes(true)) {
      return {
        status: false,
        error: "String doesn't match enums",
        string: value,
      };
    } else {
      return { status: true, error: null, string: null, bestMatch };
    }
  } else {
    return {
      status: false,
      error: 'There are no options in your enum',
      string: null,
    };
  }
};

export function delayedRetry(ms = 2000, retries = 3) {
  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) =>
        errors.pipe(
          delay(ms),
          mergeMap((error) => (retries-- > 0 ? of(error) : throwError(error)))
        )
      )
    );
}

export function tokenIsExpired(token?: string) {
  if (!token || token === 'invalid') {
    return true;
  }

  return helper.isTokenExpired(token);
}

export function decodeToken(token?: string) {
  if (!token || token === 'invalid' || tokenIsExpired(token)) {
    return true;
  }

  return helper.decodeToken(token);
}

// removeMoneyFormatting : Remove the money sign and commas for managing currency
export const removeMoneyFormatting = (inputString:string):string => {
  const cleanedString = inputString.replace(/[$,]/g, '');
  return cleanedString.trim();
}
